/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  deleteDocument,
  getDocumentFile,
  loadDocuments,
} from "../../../../services/documentsManager";
import DocumentHistoryModal from "../../../../components/DocumentsManager/DocumentHistoryModal";
import DocumentSettingsModal from "../../../../components/DocumentsManager/DocumentSettingsModal";
import DocumentsList from "../../../../components/DocumentsManager/DocumentsList";
import DocumentUploadModal from "../../../../components/DocumentsManager/DocumentUploadModal";
import {
  handleDownloadDocument,
  handleViewDocument,
} from "../../../../components/DocumentsManager/DocumentUtils";

interface prop {
  uid_compra?: string;
}

const Documents = ({ uid_compra }: prop) => {
  const entidade = "FORNECEDOR";
  const queryClient = useQueryClient();

  const [isHidden, setIsHidden] = useState<{
    uid?: string;
    isEdit?: boolean;
  }>({
    isEdit: false,
    uid: "",
  });

  const [preSelectedDocType, setPreSelectedDocType] = useState("");

  const [isModalUploadOpen, setIsModalUploadOpen] = useState(false);
  const [isModalConfigOpen, setIsModalConfigOpen] = useState(false);

  const [isModalHistoryOpen, setIsModalHistoryOpen] = useState(false);
  const [historyDocType, setHistoryDocType] = useState<string>("");

  const { data: documentsData } = useQuery({
    queryKey: ["purchasesDocuments", uid_compra],
    queryFn: () => loadDocuments(uid_compra!),
    enabled: !!uid_compra,
  });

  const { mutateAsync: deleteDocMutation } = useMutation({
    mutationFn: deleteDocument,
    onSuccess: (response) => {
      toast.success(response.message || "Documento deletado com sucesso");
      queryClient.invalidateQueries({
        queryKey: ["purchasesDocuments", uid_compra],
      });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const handleClose = () => {
    setIsHidden((prev) => ({
      ...prev,
      isEdit: false,
      uid: "",
    }));
  };
  const handleView = useCallback(async (doc: any) => {
    await handleViewDocument(doc.uid_documento, getDocumentFile);
  }, []);

  const handleDownload = useCallback(async (doc: any) => {
    await handleDownloadDocument(doc.uid_documento, getDocumentFile);
  }, []);

  const handleDeleteDocument = useCallback(
    async (doc: any) => {
      const confirm = window.confirm(
        `Deseja realmente excluir o documento "${doc.titulo}"?`,
      );

      if (confirm) {
        try {
          await deleteDocMutation(doc.uid_documento);
        } catch (error) {
          console.error(error);
        }
      }
    },
    [deleteDocMutation],
  );

  const handleUploadVersion = useCallback((doc: any) => {
    if (doc.uid_tipo_documento) {
      setPreSelectedDocType(doc.uid_tipo_documento);
      setIsModalUploadOpen(true);
    } else {
      setPreSelectedDocType("");
      setIsModalUploadOpen(true);
    }
  }, []);

  const handleHistory = useCallback((doc: any) => {
    if (doc.uid_tipo_documento) {
      setHistoryDocType(doc.uid_tipo_documento);
      setIsModalHistoryOpen(true);
    }
  }, []);

  return (
    <div>
      <DocumentsList
        data={documentsData || []}
        onAdd={() => setIsModalUploadOpen(true)}
        onSettings={() => setIsModalConfigOpen(true)}
        onView={handleView}
        onDownload={handleDownload}
        onUpload={handleUploadVersion}
        onDelete={handleDeleteDocument}
        onCopy={handleHistory}
      />
      {isHidden.isEdit && (
        <DocumentUploadModal
          entidade={entidade}
          onClose={() => handleClose()}
          uid_vinculo={(uid_compra as string) || ""}
          refetchEntity={["purchasesDocuments"]}
          initialDocType={preSelectedDocType}
        />
      )}

      {isModalConfigOpen && (
        <DocumentSettingsModal
          onClose={() => setIsModalConfigOpen(false)}
          entidade={entidade}
        />
      )}
      {isModalUploadOpen && (
        <DocumentUploadModal
          onClose={() => {
            setIsModalUploadOpen(false);
            setPreSelectedDocType("");
          }}
          onSuccess={() => {
            setIsModalUploadOpen(false);
            setPreSelectedDocType("");
            queryClient.invalidateQueries({
              queryKey: ["purchasesDocuments", uid_compra],
            });
          }}
          entidade={entidade}
          uid_vinculo={uid_compra || ""}
          initialDocType={preSelectedDocType}
        />
      )}

      {isModalHistoryOpen && (
        <DocumentHistoryModal
          onClose={() => {
            setIsModalHistoryOpen(false);
            setHistoryDocType("");
            queryClient.invalidateQueries({
              queryKey: ["purchasesDocuments", uid_compra],
            });
          }}
          entidade={entidade}
          uid_vinculo={uid_compra || ""}
          initialDocType={historyDocType}
        />
      )}
    </div>
  );
};

export default Documents;
